import { useRef, useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

import styles from './modal.module.css';

export const Modal: React.FC<ModalProps> = (props: ModalProps) => {
	const modalRef = useRef<HTMLDialogElement | null>(null);
	const { isOpen, hasCloseBtn, onClose, children } = props;
	const classNames = [styles['modal']];
	const [isModalOpen, setModalOpen] = useState(isOpen);
	if (props?.classNames) {
		for (const c of props.classNames) {
			classNames.push(c);
		}
	}
	const handleCloseModal = useCallback(() => {
		if (onClose) {
			onClose();
		}

		setModalOpen(false);
	}, [onClose]);
	useEffect(() => {
		setModalOpen(isOpen);
	}, [isOpen]);
	useEffect(() => {
		if (modalRef === null) {
			return;
		}
		const dialog = modalRef.current;
		if (!dialog) {
			return;
		}

		// Named so it can be removed. This effect re-runs whenever
		// `handleCloseModal` changes identity, so the anonymous version
		// stacked a fresh click-outside listener each time -- and every stale
		// one still closed the dialog through its own captured closure.
		const onClickOutside = (evt: MouseEvent) => {
			const rect = dialog.getBoundingClientRect();
			const isInDialog =
				rect.top <= evt.clientY &&
				evt.clientY <= rect.top + rect.height &&
				rect.left <= evt.clientX &&
				evt.clientX <= rect.left + rect.width;
			if (!isInDialog) {
				dialog.close();
				handleCloseModal();
			}
		};

		dialog.addEventListener('click', onClickOutside);
		return () => {
			dialog.removeEventListener('click', onClickOutside);
		};
	}, [modalRef, handleCloseModal]);
	useEffect(() => {
		if (modalRef === null) {
			return;
		}
		const dialog = modalRef.current;
		if (!dialog) {
			return;
		}

		if (isModalOpen) {
			dialog.showModal();
		} else {
			dialog.close();
		}
	}, [isModalOpen, modalRef]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
		if (event.key === 'Escape') {
			handleCloseModal();
		}
	};

	return (
		<dialog
			ref={modalRef}
			className={classNames.join(' ')}
			onKeyDown={handleKeyDown}
		>
			{hasCloseBtn && (
				<button
					type="button"
					title="Close"
					className={styles['modal-close-btn']}
					onClick={handleCloseModal}
				>
					<X color="var(--cf-app-color-ink-faint)" />
				</button>
			)}
			{children}
		</dialog>
	);
};

interface ModalProps {
	isOpen: boolean;
	hasCloseBtn?: boolean;
	onClose?: () => void;
	children: React.ReactNode;
	classNames?: Array<string>;
}
